import React from "react";
import { motion } from "framer-motion";
import {
  TrendingUp,
  BarChart3,
  CheckCircle,
  Globe,
  Award,
  Mail,
  Star,
  Target,
  Leaf,
  Droplets,
  FileCheck,
  Building2,
} from "lucide-react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const HIGG_Index_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0d5a55] via-[#1a7f7a] to-[#2a9b94] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-teal-400 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-cyan-500 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <TrendingUp size={18} className="text-teal-300" />
            <span>Textile & Apparel Sustainability</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-teal-300 via-cyan-300 to-emerald-300">
            HIGG Index
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
            Measure, Manage, and Improve Sustainability in Fashion & Textiles
          </p>
        </motion.div>
      </section>

      {/* What is HIGG Index */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is HIGG Index Certification?
          </h2>
          <div className="space-y-6 text-lg text-gray-700">
            <p>
              The HIGG Index is a comprehensive suite of tools developed by the
              Sustainable Apparel Coalition (SAC) for assessing and improving
              sustainability performance of fashion and textile companies. It
              measures environmental, social, and ethical practices across the
              supply chain.
            </p>
            <p>
              HIGG Index certification enables companies to evaluate their
              sustainability performance, identify improvement areas, track
              progress toward sustainability goals, and demonstrate commitment
              to responsible production aligned with global sustainability
              standards.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Key Features of HIGG Index Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Comprehensive Sustainability Assessment",
                desc: "Evaluates environmental and social performance across supply chain",
                icon: BarChart3,
              },
              {
                title: "Environmental Impact Assessment",
                desc: "Assesses water use, energy, emissions, and waste management",
                icon: Droplets,
              },
              {
                title: "Social and Labor Standards",
                desc: "Ensures ethical labor practices and worker safety",
                icon: Target,
              },
              {
                title: "Supply Chain Transparency",
                desc: "Provides visibility from suppliers to final products",
                icon: Globe,
              },
              {
                title: "Continuous Improvement",
                desc: "Enables goal setting and progress tracking over time",
                icon: TrendingUp,
              },
              {
                title: "Resource Optimization",
                desc: "Identifies inefficiencies in material and waste management",
                icon: Leaf,
              },
            ].map((feature, i) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                >
                  <IconComponent className="w-10 h-10 text-teal-600 mb-4" />
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-700">{feature.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* HIGG Index Tools */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            The HIGG Index Tools
          </h2>
          <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-8 rounded-lg">
            <p className="text-lg text-gray-700 mb-6">
              The HIGG Index comprises several specialized modules:
            </p>
            <ul className="space-y-3">
              {[
                "HIGG FEM (Facility Environmental Module) - Measures operational environmental impact",
                "HIGG MSI (Materials Sustainability Index) - Assesses material sustainability",
                "HIGG BRM (Brand & Retail Module) - Focuses on brand-level sustainability",
                "HIGG SLCP (Social & Labor Module) - Evaluates labor practices and rights",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-teal-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </section>

      {/* Importance */}
      <section className="py-16 px-4 bg-gray-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Why HIGG Index Matters
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Global Sustainability Standards - Align with international goals",
              "Improved Brand Reputation - Showcase sustainability commitment",
              "Regulatory Compliance - Meet various regional requirements",
              "Enhanced Supply Chain Collaboration - Improve partner relationships",
              "Cost Savings - Optimize resources and reduce waste",
              "Consumer Trust - Build confidence in products",
            ].map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-4"
              >
                <Star className="w-6 h-6 text-teal-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700 text-lg">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Benefits of HIGG Index Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Global Recognition in apparel and textile industries",
              "Increased Consumer Trust through ethical production",
              "Regulatory Compliance with sustainability standards",
              "Improved Operational Efficiency and cost savings",
              "Competitive Advantage in the marketplace",
              "Transparent Supply Chain visibility",
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                className="bg-gradient-to-br from-teal-100 to-cyan-100 p-6 rounded-lg"
              >
                <CheckCircle className="w-6 h-6 text-teal-700 mb-3" />
                <p className="text-gray-800 font-medium">{item}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Advantages */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Advantages of HIGG Index Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Global Industry Recognition and credibility",
              "Consumer Confidence and brand loyalty",
              "Regulatory Compliance assurance",
              "Operational Efficiency improvements",
              "Competitive Market Advantage",
              "Supplier Engagement and collaboration",
              "Resource Optimization and waste reduction",
              "Transparent Reporting to stakeholders",
            ].map((advantage, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-3 p-4 bg-white rounded-lg"
              >
                <CheckCircle className="w-5 h-5 text-teal-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{advantage}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Industries */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Industries Using HIGG Index
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Fashion and Apparel",
              "Footwear Manufacturing",
              "Textile Production",
              "Supply Chain and Logistics",
              "Retail and Distribution",
              "Material Suppliers",
            ].map((industry, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 p-4 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg"
              >
                <Building2 className="w-5 h-5 text-teal-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{industry}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* How to Achieve */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            How to Achieve HIGG Index Certification
          </h2>
          <div className="space-y-6">
            {[
              {
                step: 1,
                title: "Join Sustainable Apparel Coalition",
                desc: "Become SAC member to access HIGG Index tools",
              },
              {
                step: 2,
                title: "Complete Self-Assessment",
                desc: "Use HIGG tools to assess sustainability performance",
              },
              {
                step: 3,
                title: "Set Sustainability Goals",
                desc: "Define specific, measurable sustainability targets",
              },
              {
                step: 4,
                title: "Implement Improvements",
                desc: "Take action on identified improvement areas",
              },
              {
                step: 5,
                title: "Submit for Certification",
                desc: "Complete assessment and submit for verification",
              },
              {
                step: 6,
                title: "Ongoing Monitoring",
                desc: "Continuously monitor and report progress",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 }}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                  {item.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-r from-teal-50 via-cyan-50 to-emerald-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900 text-center">
            Why Choose MBA Solutions for HIGG Index?
          </h2>
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <p className="text-lg text-gray-700 mb-6">
              MBA Solutions provides expert HIGG Index consultancy services,
              guiding fashion and textile businesses through certification to
              enhance sustainability and improve environmental and social
              performance.
            </p>
            <ul className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "Sustainability Assessment and Gap Analysis",
                "HIGG Index Self-Assessment Guidance",
                "Environmental Strategy Development",
                "Supplier Engagement and Management",
                "Monitoring and Reporting Support",
                "Certification Assistance",
              ].map((service, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-teal-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{service}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 mb-6">
              We help fashion and textile companies achieve HIGG Index
              certification for sustainable production and ethical sourcing.
            </p>
            <div className="border-t pt-6 mt-6">
              <p className="text-gray-800 font-semibold mb-4">Get in Touch:</p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-teal-600" />
                  <span className="text-gray-700">
                    <strong>Email:</strong> atif@mbasolutions.co
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-teal-600" />
                  <span className="text-gray-700">
                    <strong>Website:</strong> www.mbasolutions.co
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </>
  );
};

export default HIGG_Index_Details;
